/*
 * Algolia custom frontend for xhawi-shopify-theme.
 * Depends on: algoliasearch-lite.umd.js, instantsearch.production.min.js, search-insights.min.js.
 * Config: window.algoliaConfig (see snippets/algolia-config.liquid).
 * Indexing is owned by the native Algolia Shopify app; this file only reads search-only indices.
 */
(function () {
  'use strict';

  var CFG = window.algoliaConfig || { enabled: false };
  if (!CFG.enabled || !window.algoliasearch) return;

  // Locale strings can arrive HTML-encoded (e.g. apostrophes as &#39;). Decode
  // them once so they display correctly whether used as text, markup or a DOM
  // property (like input.placeholder). See decodeEntities() below.
  if (CFG.strings) {
    Object.keys(CFG.strings).forEach(function (k) {
      if (typeof CFG.strings[k] === 'string') {
        CFG.strings[k] = decodeEntities(CFG.strings[k]);
      }
    });
  }

  var ARABIC_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

  // ------------------------------------------------------------------
  // Clients + analytics
  // ------------------------------------------------------------------
  var client = window.algoliasearch(CFG.appId, CFG.searchApiKey);
  // Resolve the search-insights functional interface to a *callable* aa(method, ...).
  // Depending on the build, window.AlgoliaAnalytics may be the callable itself, expose
  // it on .default, or only provide createInsightsClient(). If we can't get a function,
  // aa stays null so we never hand InstantSearch a non-callable insightsClient
  // (doing so throws "d is not a function" inside the insights middleware and breaks
  // the entire results page).
  var aa = (function resolveInsightsClient() {
    var AA = window.AlgoliaAnalytics;
    if (!AA) return null;
    if (typeof AA === 'function') return AA;
    if (typeof AA.default === 'function') return AA.default;
    if (typeof AA.createInsightsClient === 'function') {
      try {
        var req =
          (AA.getRequesterForBrowser && AA.getRequesterForBrowser()) ||
          function (data) {
            return fetch(data.url, {
              method: 'POST',
              headers: data.headers,
              body: data.data,
            });
          };
        var built = AA.createInsightsClient(req);
        if (typeof built === 'function') return built;
      } catch (e) {
        /* fall through */
      }
    }
    return null;
  })();

  if (typeof aa === 'function') {
    try {
      aa('init', {
        appId: CFG.appId,
        apiKey: CFG.searchApiKey,
        useCookie: true,
      });
      if (CFG.userToken) aa('setUserToken', String(CFG.userToken));
    } catch (e) {
      /* insights optional */
    }
  }

  function pushDataLayer(payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {
      /* noop */
    }
  }

  function sendInsights(event, params) {
    try {
      if (typeof aa === 'function') aa(event, params);
    } catch (e) {
      /* noop */
    }
  }

  // ------------------------------------------------------------------
  // Arabic query translation (mirrors arabic-search-translate.js)
  // ------------------------------------------------------------------
  function containsArabic(text) {
    return ARABIC_RE.test(text || '');
  }

  function translateViaWeglotSDK(text) {
    return new Promise(function (resolve) {
      var settled = false;
      var timeout = setTimeout(function () {
        if (!settled) {
          settled = true;
          resolve(null);
        }
      }, 2000);
      try {
        window.Weglot.translate(
          { words: [{ w: text, t: 1 }], languageTo: 'en', languageFrom: 'ar' },
          function (translations) {
            clearTimeout(timeout);
            if (!settled) {
              settled = true;
              resolve(
                translations && translations[0] && translations[0] !== text
                  ? translations[0]
                  : null
              );
            }
          }
        );
      } catch (e) {
        clearTimeout(timeout);
        if (!settled) {
          settled = true;
          resolve(null);
        }
      }
    });
  }

  function translateViaWeglotREST(text) {
    var apiKey =
      window.Weglot &&
      window.Weglot.options &&
      (window.Weglot.options.api_key || window.Weglot.options.key);
    if (!apiKey) return Promise.resolve(null);
    return fetch(
      'https://api.weglot.com/translate?api_key=' + encodeURIComponent(apiKey),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          l_from: 'ar',
          l_to: 'en',
          request_url: window.location.href,
          words: [{ w: text, t: 1 }],
        }),
      }
    )
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        var t = data && data.to_words && data.to_words[0];
        return t && t !== text ? t : null;
      })
      .catch(function () {
        return null;
      });
  }

  function translateViaMyMemory(text) {
    return fetch(
      'https://api.mymemory.translated.net/get?q=' +
        encodeURIComponent(text) +
        '&langpair=ar|en'
    )
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        var t = data && data.responseData && data.responseData.translatedText;
        return t && t !== text ? t : null;
      })
      .catch(function () {
        return null;
      });
  }

  // Merchant-editable Arabic -> English dictionary (settings.algolia_arabic_dictionary).
  var DICT = (function () {
    var map = {};
    (CFG.arabicDictionary || '')
      .split('\n')
      .forEach(function (line) {
        var parts = line.split('=');
        if (parts.length >= 2) {
          var k = parts[0].trim().toLowerCase();
          var v = parts.slice(1).join('=').trim();
          if (k && v) map[k] = v;
        }
      });
    return map;
  })();

  function dictLookup(text) {
    var key = (text || '').trim().toLowerCase();
    if (!key) return null;
    if (DICT[key]) return DICT[key];
    // Word-level: map every Arabic word we know; leave non-Arabic words as-is.
    var words = key.split(/\s+/);
    var anyArabicMapped = false;
    var mapped = words.map(function (w) {
      if (DICT[w]) {
        anyArabicMapped = true;
        return DICT[w];
      }
      if (containsArabic(w)) return null; // unknown Arabic word
      return w; // keep latin/number tokens
    });
    if (anyArabicMapped && mapped.every(Boolean)) return mapped.join(' ');
    return null;
  }

  var translateCache = {};
  function translateToEnglish(text) {
    if (!CFG.translateArabic || !containsArabic(text)) return Promise.resolve(text);
    if (translateCache[text]) return Promise.resolve(translateCache[text]);

    // 1) Dictionary (fast, store-tuned, offline).
    var dictHit = dictLookup(text);
    if (dictHit) {
      translateCache[text] = dictHit;
      return Promise.resolve(dictHit);
    }

    // 2) Weglot SDK -> 3) Weglot REST -> 4) MyMemory.
    var chain = Promise.resolve(null);
    if (
      window.Weglot &&
      typeof window.Weglot.translate === 'function' &&
      window.Weglot.initialized
    ) {
      chain = translateViaWeglotSDK(text);
    }
    return chain
      .then(function (res) {
        return res || translateViaWeglotREST(text);
      })
      .then(function (res) {
        return res || translateViaMyMemory(text);
      })
      .then(function (res) {
        var out = res || text;
        translateCache[text] = out;
        return out;
      });
  }

  // ------------------------------------------------------------------
  // Formatting helpers
  // ------------------------------------------------------------------
  // Algolia stores `price` in major units (e.g. 100 = 100 OMR). Decimal count
  // is currency-dependent (OMR = 3), so derive it instead of assuming 2.
  var MONEY_DECIMALS = (function () {
    var cur = (CFG.money && CFG.money.currency) || 'USD';
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: cur,
      }).resolvedOptions().maximumFractionDigits;
    } catch (e) {
      return 2;
    }
  })();

  function formatMoney(amountMajorUnits) {
    var format = (CFG.money && CFG.money.format) || '{{amount}}';
    var num = Number(amountMajorUnits || 0);
    function group(intStr) {
      return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    function fmt(dec) {
      var parts = num.toFixed(dec).split('.');
      return group(parts[0]) + (parts[1] ? '.' + parts[1] : '');
    }
    function commaSep(dec) {
      // swap , and . for comma-decimal locales
      return fmt(dec).replace(/,/g, 'X').replace(/\./g, ',').replace(/X/g, '.');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      switch (key) {
        case 'amount':
          return fmt(MONEY_DECIMALS);
        case 'amount_no_decimals':
          return fmt(0);
        case 'amount_with_comma_separator':
          return commaSep(MONEY_DECIMALS);
        case 'amount_no_decimals_with_comma_separator':
          return commaSep(0);
        default:
          return fmt(MONEY_DECIMALS);
      }
    });
  }

  // HTML money for cards: mirrors the theme's `money-icon` snippet — renders the
  // Omani Rial SVG icon before the amount when show_currency_code is on
  // (the store's money_format is a bare number, so the icon is the currency mark).
  function moneyHTML(amountMajorUnits) {
    var amount = formatMoney(amountMajorUnits);
    var icon = CFG.money && CFG.money.icon;
    if (CFG.showCurrencyCode && icon) {
      return (
        '<span class="omr-price inline-flex align-center gap-5" style="text-decoration: inherit; direction: ltr;">' +
        icon +
        '<span style="text-decoration: inherit;">' +
        amount +
        '</span></span>'
      );
    }
    return amount;
  }

  function localizedPath(path) {
    if (!path) return path;
    if (CFG.locale && CFG.locale.isArabic && path.indexOf('/ar') !== 0) {
      return '/ar' + (path.charAt(0) === '/' ? path : '/' + path);
    }
    return path;
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Decode HTML entities (e.g. locale strings store apostrophes as &#39;).
  // Needed when a string is assigned to a DOM *property* (like input.placeholder)
  // where the browser does not auto-decode entities the way it does in markup.
  var _decodeEl = null;
  function decodeEntities(str) {
    if (str == null || str === '') return str;
    if (String(str).indexOf('&') === -1) return String(str);
    if (!_decodeEl) _decodeEl = document.createElement('textarea');
    _decodeEl.innerHTML = String(str);
    return _decodeEl.value;
  }

  // ------------------------------------------------------------------
  // Field accessors (defensive against native app schema differences)
  // ------------------------------------------------------------------
  function hitHandle(hit) {
    return hit.handle || hit.product_handle || '';
  }
  function hitTitle(hit) {
    return hit.title || hit.product_title || '';
  }
  function hitVendor(hit) {
    return hit.vendor || hit.product_vendor || '';
  }
  function hitImage(hit) {
    return (
      hit.image ||
      hit.product_image ||
      hit.image_link ||
      (hit.images && hit.images[0]) ||
      ''
    );
  }
  function hitPrice(hit) {
    if (typeof hit.price === 'number') return hit.price;
    if (hit.price && typeof hit.price.amount === 'number') return hit.price.amount;
    if (typeof hit.price_min === 'number') return hit.price_min;
    return null;
  }
  function hitComparePrice(hit) {
    if (typeof hit.compare_at_price === 'number') return hit.compare_at_price;
    return null;
  }
  function hitProductUrl(hit) {
    var handle = hitHandle(hit);
    return localizedPath(handle ? '/products/' + handle : '#');
  }
  function hitVariantId(hit) {
    if (hit.variant_id) return hit.variant_id;
    // Algolia Shopify app: objectID is the variant id (e.g. "56692390396284"),
    // sometimes as "<productId>_<variantId>".
    if (hit.objectID) {
      if (/^\d+$/.test(hit.objectID)) return hit.objectID;
      if (/_(\d+)$/.test(hit.objectID)) return hit.objectID.match(/_(\d+)$/)[1];
    }
    return null;
  }
  function hitProductId(hit) {
    // Native Algolia Shopify app stores the product id in `id`.
    if (hit.id) return String(hit.id);
    if (hit.objectID && /^(\d+)_/.test(hit.objectID)) {
      return hit.objectID.match(/^(\d+)_/)[1];
    }
    return '';
  }
  function hitTags(hit) {
    var t = hit.tags || hit._tags || [];
    return Array.isArray(t) ? t : [];
  }
  function discountPercent(price, compare) {
    if (!compare || compare <= price) return 0;
    return Math.round(((compare - price) * 100) / compare);
  }
  /**
   * True only when the hit positively says it is out of stock.
   *
   * The index is variant-level, so `inventory_available` is the matched
   * variant's flag. It has been observed both as a real boolean and as the
   * strings "true"/"false" depending on how the Shopify Algolia app serialised
   * it, hence the loose read. Anything else — undefined, null, a missing
   * attribute — counts as in stock, so a product can never be wrongly badged
   * sold out just because the field did not come back.
   */
  function isHitSoldOut(hit) {
    if (!hit) return false;
    var available = hit.inventory_available;
    if (available === false) return true;
    if (typeof available === 'string') {
      return available.trim().toLowerCase() === 'false';
    }
    return false;
  }

  function hasTag(tags, name) {
    var lower = name.toLowerCase();
    return tags.some(function (t) {
      return String(t).toLowerCase() === lower;
    });
  }

  // Badges that mirror snippets/product-badges.liquid + product-item.liquid
  // (sale %, Omantel, Free Gift, Pre-order, Global delivery).
  function badgesHTML(hit, price, compare) {
    var card = CFG.card || {};
    var assets = CFG.assets || {};
    // `s` is local to productCardHTML; badgesHTML needs its own handle on the
    // strings or any reference throws a ReferenceError and takes the whole hit
    // render down with it.
    var s = CFG.strings || {};
    var tags = hitTags(hit);
    var vendor = (hitVendor(hit) || '').toLowerCase();
    var parts = '';

    if (card.showSale !== false) {
      var pct = discountPercent(price, compare);
      if (pct > 0) {
        parts +=
          '<div class="product__badges-sale product__badges-type-percent product__badges-inner align-self-start px-15 sale inline-flex content-center subheading_weight text-center lh-normal">-' +
          pct +
          '%</div>';
      }
    }
    // Sold out. Mirrors snippets/product-badges.liquid, which gates on the
    // show_sold_out setting AND the product being unavailable, and sits between
    // the sale badge and the pre-order/Omantel ones in the stack.
    //
    // `inventory_available` is the index's own stock flag and is already in the
    // retrieved attributes. It is read loosely — the Shopify Algolia app has
    // been seen emitting it as a boolean and as the strings "true"/"false" —
    // and an absent flag is treated as in stock so a missing attribute can
    // never mark a live product sold out.
    if (card.showSoldOut !== false && isHitSoldOut(hit)) {
      parts +=
        '<div class="product__badges-sold-out product__badges-inner align-self-start px-15 sold-out inline-flex content-center subheading_weight text-center lh-normal"' +
        ' style="--badges-color: ' +
        esc(card.soldOutColor || '') +
        ';--badges-bg:' +
        esc(card.soldOutBackground || '') +
        ';">' +
        esc(s.soldOut || 'Sold out') +
        '</div>';
    }
    if (vendor.indexOf('omantel') !== -1 && assets.omantelBadge) {
      parts +=
        '<div class="product__badges-omantel align-self-start inline-flex content-center"><img src="' +
        esc(assets.omantelBadge) +
        '" alt="Omantel" loading="lazy" width="99" height="80"></div>';
    }
    // Mirrors the Omantel pre-order badge in snippets/product-badges.liquid:
    // Omantel vendor AND one of the pre-order tags. Sits directly after the
    // Omantel vendor badge so the stacking order matches the native grid.
    // hasTag() lowercases both sides, so the 'Preorder-…' capitalised variants
    // the Liquid template has to list separately are covered here by one check.
    if (
      vendor.indexOf('omantel') !== -1 &&
      assets.omantelPreOrder &&
      (hasTag(tags, 'pre-order-omantel') ||
        hasTag(tags, 'preorder-omantel-duo'))
    ) {
      parts +=
        '<div class="product__badges-omantel align-self-start inline-flex content-center"><img src="' +
        esc(assets.omantelPreOrder) +
        '" alt="Omantel Pre-Order" loading="lazy" width="96" height="80"></div>';
    }
    if (hasTag(tags, 'FreeGift') && assets.freeGift) {
      parts +=
        '<div class="product__badges-omantel align-self-start inline-flex content-center"><img src="' +
        esc(assets.freeGift) +
        '" alt="Free Gift" loading="lazy" width="99" height="80"></div>';
    }

    var badgeWrap = parts
      ? '<div class="product__badges fs-small flex flex-wrap flex-column gap-custom pointer-none absolute top-10 top-sm-15 ' +
        (CFG.locale && CFG.locale.rtl ? 'right-10 right-sm-15' : 'left-10 left-sm-15') +
        ' z-1" style="--gap: 3;">' +
        parts +
        '</div>'
      : '';

    // Full-image corner badges (global delivery / pre-order) sit in their own
    // wrapper in the theme.
    var corner = '';
    if (hasTag(tags, 'global') && assets.globalDelivery) {
      corner +=
        '<div class="custom-global-badge"><img src="' +
        esc(assets.globalDelivery) +
        '" alt="Global delivery" loading="lazy"></div>';
    }
    if (hasTag(tags, 'pre-order') && assets.preOrder) {
      corner +=
        '<div class="custom-global-badge-preorder"><img src="' +
        esc(assets.preOrder) +
        '" alt="Pre Order" loading="lazy"></div>';
    }

    return badgeWrap + corner;
  }

  // ------------------------------------------------------------------
  // Product card markup (shared by predictive + instantsearch)
  // ------------------------------------------------------------------
  // Renders a hit using the theme's own product-card structure/classes so it
  // inherits the exact same styling as native product cards (image ratio box,
  // typography, price colours, hover behaviour). The theme's aspect-ratio CSS
  // (`[style*='--aspect-ratio']`) positions the first-child image with pure CSS,
  // so no theme JS is required for the image to render.
  function productCardHTML(hit, opts) {
    opts = opts || {};
    var card = CFG.card || {};
    var s = CFG.strings || {};
    var rtl = !!(CFG.locale && CFG.locale.rtl);
    var style = card.style || 'card';
    var align = card.alignment || 'start';
    var ratio = card.imageRatio || '1/1';
    var url = hitProductUrl(hit);
    var img = hitImage(hit);
    var title = hitTitle(hit);
    var price = hitPrice(hit);
    var compare = hitComparePrice(hit);
    var productId = hitProductId(hit);
    var quickUrl = localizedPath(
      hitHandle(hit)
        ? '/products/' + hitHandle(hit) + '?section_id=product-quickview&ajax=1'
        : '#'
    );
    var showPrice = CFG.showPrice !== false;
    var showVendor = false; // vendor name intentionally hidden on Algolia cards
    var interactive = opts.interactive !== false; // predictive turns this off

    // Price block mirrors snippets/price.liquid (card-product-price).
    var priceHTML = '';
    if (showPrice && price != null) {
      var isSale = compare && compare > price;
      priceHTML =
        '<div class="product-item__price flex align-center flex-wrap gap-5 justify-content-center" style="--gap: 3px;">' +
        '<div class="card-product-price flex align-center flex-wrap gap-5 relative">' +
        (isSale
          ? '<div class="price-regular primary-color"><span class="price heading_weight primary-color price-same-style price--special mppc">' +
            moneyHTML(price) +
            '</span></div>' +
            '<div class="price-regular"><s class="price-item compare-price dark-grey">' +
            moneyHTML(compare) +
            '</s></div>'
          : '<div class="price-regular"><span class="price price-same-style heading-style">' +
            moneyHTML(price) +
            '</span></div>') +
        '</div></div>';
    }

    // Hover actions, replicating the native card exactly: one
    // `.product-item__action` container holds the icon column (wishlist +
    // quick view, absolute top on desktop) AND the full-width Select Options
    // bar (absolute bottom). This prevents the icons and the select overlay
    // from colliding (the earlier bug where the eye icon was cut off).
    var side = rtl ? 'left' : 'right';
    var actions = '';
    if (interactive) {
      var iconClass =
        'invisible-1025 product__action-animation transition product-item__button pointer tooltip relative btn-hover btn-white rounded-50 p-0 shadow w-45 h-45 inline-flex content-center';
      var actionInner = '';
      if (card.showWishlist !== false && productId) {
        actionInner +=
          '<button-wishlist tabindex="0" class="' +
          iconClass +
          ' product-item__button-wishlist" data-product-id="' +
          esc(productId) +
          '" data-action="go_to_page">' +
          '<svg width="14" height="13" fill="none"><use href="#icon-wishlist" /></svg>' +
          '<span class="tooltip-content invisible rounded-3 absolute pointer-none' +
          (rtl ? ' tooltip-right' : ' tooltip-left') +
          '">' +
          esc(s.wishlistAdd || 'Add to wishlist') +
          '</span>' +
          '</button-wishlist>';
      }
      if (card.showQuickView !== false) {
        actionInner +=
          '<button-quickview data-custom-class="quickview" tabindex="0" class="' +
          iconClass +
          ' product-item__button-quickview" data-url="' +
          esc(quickUrl) +
          '">' +
          '<svg width="14" height="12" fill="none" class="hidden-on-load"><use href="#icon-quickview" /></svg>' +
          '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" class="spin opacity-0 icon-load"><use href="#icon-load"></use></svg>' +
          '<span class="tooltip-content invisible rounded-3 absolute pointer-none' +
          (rtl ? ' tooltip-right' : ' tooltip-left') +
          '">' +
          esc(s.quickView || 'Quick view') +
          '</span>' +
          '</button-quickview>';
      }

      // Full-width Select Options overlay (opens theme quick-buy).
      var selectBtn = '';
      // Pre-order duo products cannot be bought from a listing — they must go
      // through the BRE builder on the PDP. Mirrors the show_add_cart gate in
      // snippets/product-item.liquid so Algolia-rendered grids behave the same
      // as native ones. `tags` is in the index's retrieved attributes.
      if (
        opts.showAddToCart !== false &&
        !hasTag(hitTags(hit), 'preorder-omantel-duo')
      ) {
        // Mirrors native product-item.liquid exactly: on mobile this is a 45x45
        // circular white cart-icon button (w-45 h-45 + text hidden); on desktop
        // (>=1025) it becomes the auto-width "Select options" bar shown on hover.
        // Missing w-45 h-45 + the mobile text-hiding classes was making the full
        // "Select options" label overlap the image on phones.
        selectBtn =
          '<select-option tabindex="0" data-custom-class="quickview" data-item="overlay-quickbuy" data-url="' +
          esc(quickUrl) +
          '" class="invisible-1025 product__add-cart product-item__select-options transition btn btn-white shadow text-center w-45 h-45 w-1025-auto h-1025-auto inline-flex content-center relative absolute-1025 z-5 bottom-1025-15 left-1025-15 right-1025-15">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" class="spin opacity-0 icon-load"><use href="#icon-load"></use></svg>' +
          '<svg width="12" height="14" fill="none" class="block hidden-1025 hidden-on-load icon-cart"><use href="#icon-cart" /></svg>' +
          '<span class="hidden block-1025 hidden-on-load">' +
          esc(s.selectOptions || 'Select options') +
          '</span>' +
          '</select-option>';
      }

      if (actionInner || selectBtn) {
        actions =
          '<div class="product-item__action transition flex flex-column gap-5 absolute static-1025 bottom-10 ' +
          side +
          '-10">' +
          (actionInner
            ? '<div class="product-item__button-action heading-color transition flex flex-column gap-5 absolute-1025 z-5 bottom-1025-auto top-1025-15 ' +
              side +
              '-1025-15">' +
              actionInner +
              '</div>'
            : '') +
          selectBtn +
          '</div>';
      }
    }

    var mediaHTML =
      '<a href="' +
      esc(url) +
      '" aria-label="' +
      esc(title) +
      '" class="product__media product-item__media--ratio rounded block" ' +
      'style="--aspect-ratio: ' +
      esc(ratio) +
      ';" data-algolia-hit-link>' +
      (img
        ? '<img class="product-image first-image" loading="lazy" src="' +
          esc(img) +
          '" alt="' +
          esc(title) +
          '" width="400" height="400">'
        : '') +
      '</a>';

    return (
      '<div class="product-item product__item-js style-as-' +
      esc(style) +
      '" data-object-id="' +
      esc(hit.objectID || '') +
      '" data-product-id="' +
      esc(productId) +
      '" data-position="' +
      (opts.position || '') +
      '">' +
      '<div class="product-item__wrapper block product-grid ' +
      (style === 'card' ? 'style-as-card' : '') +
      '">' +
      '<div class="product-item__inner hover-effect overflow-hidden relative">' +
      badgesHTML(hit, price, compare) +
      mediaHTML +
      actions +
      '</div>' +
      '<div class="product-item__information word-break text-' +
      esc(align) +
      ' mt-15">' +
      (showVendor && hitVendor(hit)
        ? '<div class="product-item__vendor dark-grey">' +
          esc(hitVendor(hit)) +
          '</div>'
        : '') +
      '<a href="' +
      esc(url) +
      '" aria-label="' +
      esc(title) +
      '" class="product-item__name heading-style no-underline fs-custom block lh-normal" data-algolia-hit-link>' +
      esc(title) +
      '</a>' +
      priceHTML +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  // ------------------------------------------------------------------
  // Add to cart (matches theme AJAX cart flow)
  // ------------------------------------------------------------------
  function addToCart(variantId, objectId, queryId) {
    var routes = CFG.routes || {};
    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    return fetch(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function () {
        sendInsights('convertedObjectIDsAfterSearch', {
          index: CFG.indices.products,
          eventName: 'Product Added To Cart',
          objectIDs: [objectId],
          queryID: queryId,
        });
        pushDataLayer({ event: 'algolia_add_to_cart', object_id: objectId });
        // Refresh minicart if the theme cart element exists.
        if (window.location) {
          var minicart = document.querySelector('cart-notification, #minicart_wrapper');
          if (minicart && typeof window.fetchConfig === 'undefined') {
            // Soft refresh count badge.
            fetch('/cart.json')
              .then(function (r) {
                return r.json();
              })
              .then(function (cart) {
                document.querySelectorAll('.cart-count').forEach(function (el) {
                  el.textContent = cart.item_count;
                });
              })
              .catch(function () {});
          }
          try {
            document.dispatchEvent(new CustomEvent('algolia:added-to-cart'));
          } catch (e) {}
        }
      })
      .catch(function () {});
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-algolia-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var vid = btn.getAttribute('data-variant-id');
    var oid = btn.getAttribute('data-object-id');
    if (vid) addToCart(vid, oid, btn.getAttribute('data-query-id'));
  });

  // Track clicks on hits for analytics.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-algolia-hit-link]');
    if (!link) return;
    var card = link.closest('[data-object-id]');
    if (!card) return;
    var oid = card.getAttribute('data-object-id');
    if (!oid) return;
    var qid = card.getAttribute('data-query-id');
    if (qid) {
      sendInsights('clickedObjectIDsAfterSearch', {
        index: CFG.indices.products,
        eventName: 'Product Clicked',
        objectIDs: [oid],
        positions: [Number(card.getAttribute('data-position')) || 1],
        queryID: qid,
      });
    } else {
      sendInsights('clickedObjectIDs', {
        index: CFG.indices.products,
        eventName: 'Product Clicked',
        objectIDs: [oid],
      });
    }
    pushDataLayer({ event: 'algolia_product_click', object_id: oid });
  });

  // ------------------------------------------------------------------
  // Predictive / header autocomplete (custom, theme-native markup)
  // ------------------------------------------------------------------
  function linkListHTML(items, hrefFn, labelFn) {
    return (
      '<ul class="list-unstyled">' +
      items
        .map(function (it) {
          return (
            '<li><a class="no-underline" href="' +
            esc(hrefFn(it)) +
            '">' +
            esc(labelFn(it)) +
            '</a></li>'
          );
        })
        .join('') +
      '</ul>'
    );
  }

  // Two-column predictive layout:
  //   LEFT  -> Trending Search + All Categories
  //   RIGHT -> Products (search results while typing, trending when empty)
  function buildPredictiveMarkup(opts) {
    var s = CFG.strings || {};
    var trendingSearches = opts.trendingSearches || [];
    var products = opts.products || [];
    var term = opts.term || '';
    var isEmpty = !!opts.isEmpty;
    var searchBase = CFG.routes.search || '/search';

    var left = '';
    if (trendingSearches.length) {
      left +=
        '<div class="algolia-predictive__group algolia-trending">' +
        '<div class="algolia-predictive__title h6">' +
        esc(s.trendingSearches || 'Trending Search') +
        '</div>' +
        linkListHTML(
          trendingSearches.slice(0, 6),
          function (q) {
            return localizedPath(searchBase + '?q=' + encodeURIComponent(q));
          },
          function (q) {
            return q;
          }
        ) +
        '</div>';
    }
    var right =
      '<div class="algolia-predictive__group algolia-products">' +
      '<div class="algolia-predictive__title h6">' +
      esc(isEmpty ? s.popularProducts || 'Trending Products' : s.products || 'Products') +
      '</div>';
    if (products.length) {
      right +=
        '<div class="algolia-predictive__grid search-results-list grid grid-cols gap-15" ' +
        'style="--col-number:2;--col-tablet:2;--col-desktop-small:2;--col-desktop:3;">';
      products.slice(0, 9).forEach(function (hit, i) {
        right += productCardHTML(hit, {
          showVendor: true,
          position: i + 1,
          showAddToCart: false,
          interactive: false,
        });
      });
      right += '</div>';
      if (!isEmpty) {
        right +=
          '<div class="algolia-predictive__viewall mt-15">' +
          '<a class="btn-outline btn-rounded no-underline" href="' +
          esc(localizedPath(searchBase + '?q=' + encodeURIComponent(term))) +
          '">' +
          esc(s.viewAll || 'View all Results') +
          '</a></div>';
      }
    } else {
      right +=
        '<div class="algolia-predictive__empty">' +
        esc(s.noResults || 'No products found') +
        '</div>';
    }
    right += '</div>';

    var html =
      '<div class="algolia-predictive__layout">' +
      (left
        ? '<div class="algolia-predictive__col algolia-predictive__col--left">' +
          left +
          '</div>'
        : '') +
      '<div class="algolia-predictive__col algolia-predictive__col--right">' +
      right +
      '</div>' +
      '</div>';

    return { html: html, queryId: opts.queryId };
  }

  function stampQueryId(container, queryId) {
    if (!queryId) return;
    container.querySelectorAll('[data-object-id]').forEach(function (el) {
      el.setAttribute('data-query-id', queryId);
    });
  }

  function weglotRefresh() {
    if (
      CFG.locale &&
      CFG.locale.isArabic &&
      window.Weglot &&
      typeof window.Weglot.refresh === 'function'
    ) {
      try {
        window.Weglot.refresh();
      } catch (e) {}
    }
  }

  // Popularity replica used to derive "trending" data from Algolia.
  function popularIndexName() {
    var base = CFG.indices.products;
    var lines = (CFG.sortOrders || '').split('\n');
    for (var i = 0; i < lines.length; i++) {
      var name = (lines[i].split('|')[0] || '').trim();
      if (name && name !== base) return name;
    }
    return base + '_recently_ordered_count_desc';
  }

  // Trending Search + All Categories + Trending Products, all from Algolia.
  // Cached for the session so it isn't re-fetched on every keystroke.
  var _trendingPromise = null;
  function fetchTrending() {
    if (_trendingPromise) return _trendingPromise;

    // Popular products (isolated so a missing replica falls back to base index).
    var productsPromise = client
      .search([
        { indexName: popularIndexName(), query: '', params: { hitsPerPage: 12 } },
      ])
      .then(function (res) {
        return (res.results && res.results[0] && res.results[0].hits) || [];
      })
      .catch(function () {
        return client
          .search([
            {
              indexName: CFG.indices.products,
              query: '',
              params: { hitsPerPage: 12 },
            },
          ])
          .then(function (res) {
            return (res.results && res.results[0] && res.results[0].hits) || [];
          })
          .catch(function () {
            return [];
          });
      });

    var suggestionsPromise = CFG.indices.querySuggestions
      ? client
          .search([
            {
              indexName: CFG.indices.querySuggestions,
              query: '',
              params: { hitsPerPage: 8 },
            },
          ])
          .then(function (res) {
            return (res.results && res.results[0] && res.results[0].hits) || [];
          })
          .catch(function () {
            return [];
          })
      : Promise.resolve([]);

    _trendingPromise = Promise.all([
      productsPromise,
      suggestionsPromise,
    ]).then(function (out) {
      var products = out[0];
      var suggestionHits = out[1];
      var searches = suggestionHits
        .map(function (h) {
          return h.query || h.q;
        })
        .filter(Boolean);
      // Fall back to trending product types when no Query Suggestions index.
      if (!searches.length) {
        var seen = {};
        products.forEach(function (p) {
          // Strip internal parenthetical markers e.g. "Mobiles (OT)" -> "Mobiles".
          var t = (p.product_type || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
          if (t && !seen[t.toLowerCase()]) {
            seen[t.toLowerCase()] = 1;
            searches.push(t);
          }
        });
      }
      return {
        products: products,
        searches: searches.slice(0, 6),
      };
    });
    return _trendingPromise;
  }

  function runPredictive(rawTerm, container, statusEl) {
    var term = (rawTerm || '').trim();
    fetchTrending().then(function (tr) {
      if (!term) {
        // Empty / focus state: trending searches + trending products.
        var emptyBuilt = buildPredictiveMarkup({
          trendingSearches: tr.searches,
          products: tr.products,
          term: '',
          isEmpty: true,
        });
        container.innerHTML = emptyBuilt.html;
        if (statusEl) statusEl.textContent = '';
        weglotRefresh();
        return;
      }
      translateToEnglish(term).then(function (translated) {
        // Products (isolated so a collections failure can't break results).
        var productsPromise = client
          .search([
            {
              indexName: CFG.indices.products,
              query: translated,
              params: { hitsPerPage: 9, clickAnalytics: true },
            },
          ])
          .then(function (res) {
            var r = (res.results && res.results[0]) || {};
            return { hits: r.hits || [], queryID: r.queryID || null };
          })
          .catch(function () {
            return { hits: [], queryID: null };
          });

        productsPromise.then(function (productData) {
          var built = buildPredictiveMarkup({
            trendingSearches: tr.searches,
            products: productData.hits,
            term: term,
            queryId: productData.queryID,
            isEmpty: false,
          });
          container.innerHTML = built.html;
          stampQueryId(container, productData.queryID);
          if (statusEl) {
            statusEl.textContent = productData.hits.length + ' results';
          }
          weglotRefresh();
        });
      });
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  // Stop the native theme predictive search from fetching/rendering so it
  // doesn't fight the Algolia results in the same container.
  function neutralizeNativePredictive() {
    try {
      if (typeof window.BlsSearchShopify !== 'undefined') {
        if (typeof window.BlsSearchShopify.getSearchResults === 'function') {
          window.BlsSearchShopify.getSearchResults = function () {};
        }
        if (typeof window.BlsSearchShopify.renderSearchResults === 'function') {
          window.BlsSearchShopify.renderSearchResults = function () {};
        }
      }
    } catch (e) {
      /* noop */
    }
  }

  function initPredictive() {
    if (!CFG.surfaces || !CFG.surfaces.autocomplete) return;
    neutralizeNativePredictive();
    // Drop the theme's own "Trending Search" chips; Algolia renders its own.
    document.querySelectorAll('#quick-search').forEach(function (el) {
      el.remove();
    });
    var inputs = document.querySelectorAll(
      '#search-template, form[role="search"] input[name="q"], .search-modal__form input[name="q"]'
    );
    if (!inputs.length) return;

    inputs.forEach(function (input) {
      if (input.__algoliaBound) return;
      input.__algoliaBound = true;

      // Find nearest predictive container, or create one.
      var scope =
        input.closest('.search__type-popup') ||
        input.closest('.block-search') ||
        input.closest('form').parentNode;
      var container = scope
        ? scope.querySelector('[data-predictive-search]')
        : null;
      if (!container) {
        container = document.createElement('div');
        container.setAttribute('data-predictive-search', '');
        input.closest('form').insertAdjacentElement('afterend', container);
      }
      var statusEl = scope
        ? scope.querySelector('.predictive-search-status')
        : null;

      var handler = debounce(function () {
        runPredictive(input.value, container, statusEl);
      }, 250);
      input.addEventListener('input', handler);
      input.addEventListener('focus', function () {
        // Show trending (empty) or results (typed) as soon as the box is focused.
        runPredictive(input.value, container, statusEl);
      });

      // Intercept submit to keep Arabic original in URL.
      var form = input.closest('form');
      if (form && !form.__algoliaBound) {
        form.__algoliaBound = true;
        form.addEventListener(
          'submit',
          function (e) {
            var q = input.value.trim();
            if (!q) return;
            if (!CFG.translateArabic || !containsArabic(q)) return;
            e.preventDefault();
            translateToEnglish(q).then(function (translated) {
              var url = new URL(
                form.action || CFG.routes.search,
                window.location.origin
              );
              url.searchParams.set('q', translated);
              url.searchParams.set('original_q', q);
              window.location.href = url.toString();
            });
          },
          true
        );
      }
    });
  }

  // ------------------------------------------------------------------
  // InstantSearch (results page + collection pages)
  // ------------------------------------------------------------------
  function parseFacets() {
    var out = [];
    (CFG.facets || '')
      .split('\n')
      .map(function (l) {
        return l.trim();
      })
      .filter(Boolean)
      .forEach(function (line) {
        var parts = line.split('|');
        out.push({ attribute: parts[0].trim(), label: (parts[1] || parts[0]).trim() });
      });
    return out;
  }

  function initInstantSearch(mount) {
    if (!window.instantsearch) return;
    var instantsearch = window.instantsearch;
    var widgets = instantsearch.widgets;
    var connectors = instantsearch.connectors;

    var collectionHandle = mount.getAttribute('data-collection-handle') || '';
    var s = CFG.strings || {};
    var facets = parseFacets();

    // Results use an infinite-scroll grid (accumulating pages), so a deep link
    // like ?...[page]=8 would make infiniteHits render ONLY page 8 and skip
    // 1-7. Strip any [page] param from the URL before InstantSearch routing
    // reads it, so we always start at page 1 and load forward from there.
    try {
      var _url = new URL(window.location.href);
      var _drop = [];
      _url.searchParams.forEach(function (v, k) {
        if (/\[page\]$/.test(k)) _drop.push(k);
      });
      if (_drop.length) {
        _drop.forEach(function (k) {
          _url.searchParams.delete(k);
        });
        window.history.replaceState({}, '', _url.toString());
      }
    } catch (e) {}

    // Seed the initial query from the Shopify search URL (?q= / ?original_q=).
    // Default InstantSearch routing does NOT read Shopify's ?q= param, so
    // without this the results page loads with an empty query.
    var seedQuery = '';
    try {
      var params = new URLSearchParams(window.location.search);
      seedQuery = params.get('original_q') || params.get('q') || '';
    } catch (e) {
      seedQuery = '';
    }
    var initialUiState = {};
    if (seedQuery && !collectionHandle) {
      var idxState = {};
      idxState.query = seedQuery;
      initialUiState[CFG.indices.products] = idxState;
    }

    var search = instantsearch({
      indexName: CFG.indices.products,
      searchClient: buildTranslatingSearchClient(),
      routing: true,
      // Only enable the insights middleware when we have a genuine callable client.
      // Passing a non-function insightsClient throws inside InstantSearch and blanks
      // the results grid, so fall back to no middleware (analytics stays optional).
      insights: typeof aa === 'function' ? { insightsClient: aa } : false,
      initialUiState: initialUiState,
      future: { preserveSharedStateOnUnmount: true },
    });

    var widgetList = [];

    // Hidden configure for base filters (collection scoping).
    // distinct:1 collapses variant-level records to one per product *server-side*
    // (so nbHits/counts reflect unique products). It is a no-op until the index
    // has `attributeForDistinct` set (e.g. to "id") — see note in the summary to
    // the merchant. The client-side dedupe below is the fallback until then.
    var configureParams = {
      hitsPerPage: mount.getAttribute('data-hits-per-page') || 24,
      clickAnalytics: true,
      distinct: 1,
      // Compute facet counts *after* distinct so the sidebar shows unique
      // product counts (e.g. Maryana 16) instead of variant counts (254).
      facetingAfterDistinct: true,
    };
    if (collectionHandle) {
      configureParams.filters = 'collections:' + collectionHandle;
    }
    widgetList.push(widgets.configure(configureParams));

    // Search box (results page only; collections rely on their title).
    var sbEl = mount.querySelector('[data-algolia-searchbox]');
    if (sbEl) {
      widgetList.push(
        widgets.searchBox({
          container: sbEl,
          placeholder: decodeEntities(s.searchPlaceholder) || 'Search',
          showSubmit: true,
          showReset: true,
          // Reuse the theme's search field look (rounded field + icon buttons).
          cssClasses: {
            form: 'search search-modal__form field flex relative btn-rounded',
            input: 'search__input field__input flex-1',
            submit:
              'search__button field__button btn-reset w-44 content-center pointer',
            reset:
              'search__reset btn-reset w-44 content-center pointer',
            loadingIndicator: 'loading-icon',
          },
          templates: {
            submit:
              '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="transition"><use href="#icon-search"/></svg>',
            reset:
              '<svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor"><use href="#icon-close"/></svg>',
            loadingIndicator:
              '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" class="spin"><use href="#icon-load"></use></svg>',
          },
          // Clear all active filters whenever the search term changes, so a new
          // search always starts from a clean filter state. (No translation
          // here: the box keeps the raw/Arabic text and
          // buildTranslatingSearchClient() translates at query time.)
          queryHook: function (query, refine) {
            var current = (search.helper && search.helper.state.query) || '';
            if (query !== current && search.helper) {
              search.helper.clearRefinements();
            }
            refine(query);
          },
        })
      );
    }

    var statsEl = mount.querySelector('[data-algolia-stats]');
    if (statsEl) widgetList.push(widgets.stats({ container: statsEl }));

    var sortEl = mount.querySelector('[data-algolia-sortby]');
    if (sortEl) {
      var sortItems = (CFG.sortOrders || '')
        .split('\n')
        .map(function (l) {
          return l.trim();
        })
        .filter(Boolean)
        .map(function (line) {
          var parts = line.split('|');
          return {
            value: parts[0].trim(),
            label: (parts[1] || parts[0]).trim(),
          };
        })
        .filter(function (it) {
          return it.value;
        });
      // Ensure the base index is the first/default entry.
      if (!sortItems.length) {
        sortItems = [{ value: CFG.indices.products, label: 'Relevance' }];
      }
      if (sortItems[0].value !== CFG.indices.products) {
        sortItems.unshift({ value: CFG.indices.products, label: 'Relevance' });
      }
      widgetList.push(widgets.sortBy({ container: sortEl, items: sortItems }));
    }

    // Facets sidebar. Facets render in the exact order of the config; the price
    // attribute renders as a range slider wherever it appears in that order
    // (instead of being hardcoded last), so it can match the native layout.
    var facetsEl = mount.querySelector('[data-algolia-facets]');
    if (facetsEl) {
      facets.forEach(function (f) {
        var wrap = document.createElement('div');
        wrap.className = 'algolia-facet';
        wrap.innerHTML =
          '<div class="algolia-facet__title h6">' + esc(f.label) + '</div>';
        var body = document.createElement('div');
        wrap.appendChild(body);
        facetsEl.appendChild(wrap);

        var isPrice = CFG.priceAttribute && f.attribute === CFG.priceAttribute;
        if (isPrice) {
          widgetList.push(
            widgets.rangeSlider({
              container: body,
              attribute: f.attribute,
              tooltips: {
                format: function (value) {
                  return formatMoney(value);
                },
              },
              pips: false,
            })
          );
        } else {
          widgetList.push(
            widgets.refinementList({
              container: body,
              attribute: f.attribute,
              searchable: false,
              showMore: true,
              limit: 8,
              cssClasses: { showMore: 'btn-link' },
              templates: {
                showMoreText: function (data) {
                  return data.isShowingMore
                    ? s.showLess || 'Show less'
                    : s.showMore || 'Show more';
                },
              },
            })
          );
          // Hide a refinement facet whose attribute has no values for the
          // current results (e.g. metafields that aren't synced yet), so we
          // never show an empty filter box with just a title.
          search.on('render', function () {
            var hasItems = body.querySelector('.ais-RefinementList-item');
            wrap.style.display = hasItems ? '' : 'none';
          });
        }
      });

      var clearEl = document.createElement('div');
      facetsEl.appendChild(clearEl);
      widgetList.push(
        widgets.clearRefinements({
          container: clearEl,
          templates: { resetLabel: s.clearAll || 'Clear all' },
          cssClasses: { button: 'btn-outline btn-rounded' },
        })
      );
    }

    // Hits.
    var hitsEl = mount.querySelector('[data-algolia-hits]');
    if (hitsEl) {
      widgetList.push(
        widgets.infiniteHits({
          container: hitsEl,
          templates: {
            item: function (hit, bindEvent) {
              return productCardHTML(hit, {
                showVendor: true,
                showAddToCart: true,
                position: hit.__position,
              });
            },
            empty: function () {
              return (
                '<div class="algolia-empty">' +
                esc(s.noResults || 'No products found') +
                '</div>'
              );
            },
            showMoreText: function () {
              return s.viewAll || 'Show more';
            },
          },
          cssClasses: {
            list: 'search-results-list grid grid-cols gap product-grid',
          },
          transformItems: function (items, ctx) {
            return items.map(function (item) {
              item.__queryID = ctx.results && ctx.results.queryID;
              return item;
            });
          },
        })
      );
    }

    search.addWidgets(widgetList);
    search.start();

    // Mobile "Filters" toggle: collapse the sidebar behind a button on small
    // screens (the button + sidebar visibility are handled in CSS via media
    // queries; this just flips the `is-open` state).
    var filterToggle = mount.querySelector('[data-algolia-filter-toggle]');
    var sidebarEl = mount.querySelector('.algolia-sidebar');
    if (filterToggle && sidebarEl) {
      filterToggle.addEventListener('click', function () {
        var open = sidebarEl.classList.toggle('is-open');
        filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    // ----------------------------------------------------------------
    // Native-style "Show more" load-more (progress bar + count), mirroring
    // snippets/pagination.liquid. The InstantSearch loadMore button stays
    // hidden and is clicked programmatically (by this button or on scroll).
    // ----------------------------------------------------------------
    var scrollState = { loading: false };
    var loadMoreState = {};
    var loadMoreUI = null;
    if (hitsEl) {
      loadMoreUI = document.createElement('div');
      loadMoreUI.className = 'pagination-loadmore mt-50 text-center';
      loadMoreUI.setAttribute('data-algolia-loadmore', '');
      loadMoreUI.innerHTML =
        '<div class="load-more-bar">' +
        '<span class="loadmore-amount" data-algolia-loadmore-amount></span>' +
        '<div class="load-more-bar-percent w-custom max-w-100 mx-auto h-2 border-bg btn-rounded relative mt-10" style="--custom-width: 30rem;">' +
        '<span class="load-more-percent absolute inset-y-0 left-0 heading-bg inline-block btn-rounded transition" data-algolia-loadmore-percent style="width:0%"></span>' +
        '</div>' +
        '</div>' +
        '<button type="button" class="inline-flex mt-30 relative actions-loadmore no-underline btn-primary loadmore" data-algolia-loadmore-btn>' +
        '<span class="hidden-on-load">' +
        esc(s.showMore || 'Show more') +
        '</span>' +
        '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" class="spin opacity-0 icon-load"><use href="#icon-load"></use></svg>' +
        '</button>';
      hitsEl.parentNode.insertBefore(loadMoreUI, hitsEl.nextSibling);

      var triggerLoadMore = function () {
        if (scrollState.loading) return;
        var btn = hitsEl.querySelector('.ais-InfiniteHits-loadMore');
        if (
          btn &&
          !btn.disabled &&
          !btn.classList.contains('ais-InfiniteHits-loadMore--disabled')
        ) {
          // Record the current count so we only allow the *next* load once the
          // list has actually grown. Clicking again before the page resolves
          // makes infiniteHits see a non-contiguous page jump and reset its
          // cache (the "items stuck at 12, page racing" bug).
          scrollState.loading = true;
          scrollState.lastCount = hitsEl.querySelectorAll('.product-item').length;
          btn.click();
          // Safety net: if the expected growth never arrives (e.g. a refinement
          // change resets the list mid-flight), auto-release the lock so
          // load-more can never get permanently stuck.
          clearTimeout(scrollState.timer);
          scrollState.timer = setTimeout(function () {
            scrollState.loading = false;
          }, 4000);
        }
      };

      loadMoreUI
        .querySelector('[data-algolia-loadmore-btn]')
        .addEventListener('click', triggerLoadMore);

      // Auto-load on scroll as well (keeps the button as the visible affordance).
      var sentinel = document.createElement('div');
      sentinel.className = 'algolia-infinite-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      loadMoreUI.parentNode.insertBefore(sentinel, loadMoreUI);
      loadMoreState.sentinel = sentinel;
      loadMoreState.trigger = triggerLoadMore;
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting && !scrollState.loading) triggerLoadMore();
            });
          },
          { rootMargin: '800px 0px' }
        );
        io.observe(sentinel);
      }
      // Fallback + re-arm: IntersectionObserver only fires on *changes*, so if
      // the sentinel stays in view after a load it won't fire again. A light
      // scroll listener plus a post-render check keep the chain going.
      window.addEventListener(
        'scroll',
        function () {
          maybeAutoLoad();
        },
        { passive: true }
      );
    }

    // Trigger another load if the sentinel is within (or near) the viewport.
    function maybeAutoLoad() {
      var sen = loadMoreState.sentinel;
      if (!sen || scrollState.loading || !loadMoreState.trigger) return;
      var rect = sen.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top <= vh + 800) loadMoreState.trigger();
    }

    function refreshLoadMoreUI() {
      if (!hitsEl || !loadMoreUI) return;
      var total = 0;
      try {
        total =
          (search.helper.lastResults && search.helper.lastResults.nbHits) || 0;
      } catch (e) {}
      var loaded = hitsEl.querySelectorAll('.product-item').length;

      // Whenever the query or any refinement changes, infiniteHits resets its
      // list back to the first page. Detect that via a signature and reset the
      // load lock + baseline, otherwise a pending lock from a previous
      // (auto)load stays stuck true and every future load-more bails out —
      // which is exactly what breaks load-more after applying a filter.
      var sig = '';
      try {
        var st = search.helper.state;
        sig = JSON.stringify([
          st.query || '',
          st.disjunctiveFacetsRefinements || {},
          st.facetsRefinements || {},
          st.numericRefinements || {},
          st.hierarchicalFacetsRefinements || {},
        ]);
      } catch (e) {}
      if (sig !== scrollState.sig) {
        scrollState.sig = sig;
        scrollState.loading = false;
        scrollState.lastCount = loaded;
      }

      var amountEl = loadMoreUI.querySelector('[data-algolia-loadmore-amount]');
      var percentEl = loadMoreUI.querySelector('[data-algolia-loadmore-percent]');
      var btn = loadMoreUI.querySelector('[data-algolia-loadmore-btn]');
      if (amountEl) {
        var tpl = s.resultCount || "You've viewed AMOUNT of COUNT result";
        amountEl.textContent = tpl
          .replace('AMOUNT', loaded)
          .replace('COUNT', total);
      }
      if (percentEl) {
        var pct = total ? Math.round((loaded * 100) / total) : 0;
        percentEl.style.width = pct + '%';
      }
      var hasMore = false;
      var aisBtn = hitsEl.querySelector('.ais-InfiniteHits-loadMore');
      if (aisBtn) {
        hasMore =
          !aisBtn.disabled &&
          !aisBtn.classList.contains('ais-InfiniteHits-loadMore--disabled');
      }
      loadMoreUI.style.display = total > 0 ? '' : 'none';
      if (btn) btn.style.display = hasMore ? '' : 'none';

      // Release the load lock only after the list has actually grown (or there
      // is nothing more to load), so we never fire overlapping load-mores.
      if (
        scrollState.loading &&
        (loaded > (scrollState.lastCount || 0) || !hasMore)
      ) {
        scrollState.loading = false;
        clearTimeout(scrollState.timer);
      }
    }

    // The product index is variant-level (objectID = variant, id = product),
    // so multi-variant products (e.g. a lipstick with many shades) return one
    // record per variant and repeat in the grid. Collapse to one card per
    // product id. (Server-side `distinct` would need attributeForDistinct set
    // on the Algolia index + reindex, which is an app/admin task.)
    function dedupeHits() {
      if (!hitsEl) return;
      var seen = {};
      var items = hitsEl.querySelectorAll('.product-item[data-product-id]');
      for (var i = 0; i < items.length; i++) {
        var pid = items[i].getAttribute('data-product-id');
        if (!pid) continue;
        // Hide the parent grid cell (ais-InfiniteHits-item li), not just the
        // inner card — removing only the inner div leaves an empty <li> slot
        // that creates visible gaps in the CSS grid.
        var cell = items[i].closest
          ? items[i].closest('.ais-InfiniteHits-item') || items[i].parentNode
          : items[i].parentNode;
        if (seen[pid]) {
          cell.style.display = 'none';
        } else {
          seen[pid] = true;
          cell.style.display = '';
        }
      }
    }

    // Re-stamp queryID + Weglot refresh + update load-more after each render.
    search.on('render', function () {
      if (hitsEl) {
        dedupeHits();
        var qid = null;
        try {
          qid = search.helper.lastResults && search.helper.lastResults.queryID;
        } catch (e) {}
        stampQueryId(hitsEl, qid);
      }
      refreshLoadMoreUI();
      // Keep auto-loading while the sentinel remains in view (IO won't re-fire).
      setTimeout(maybeAutoLoad, 60);
      if (
        CFG.locale &&
        CFG.locale.isArabic &&
        window.Weglot &&
        typeof window.Weglot.refresh === 'function'
      ) {
        try {
          window.Weglot.refresh();
        } catch (e) {}
      }
    });
  }

  // A search client that translates Arabic queries before hitting Algolia.
  function buildTranslatingSearchClient() {
    var base = client;
    if (!CFG.translateArabic) return base;
    return Object.assign({}, base, {
      search: function (requests) {
        var needsTranslate = requests.some(function (r) {
          return containsArabic(r.params && r.params.query);
        });
        if (!needsTranslate) return base.search(requests);
        return Promise.all(
          requests.map(function (r) {
            var q = r.params && r.params.query;
            if (q && containsArabic(q)) {
              return translateToEnglish(q).then(function (t) {
                r.params.query = t;
                return r;
              });
            }
            return Promise.resolve(r);
          })
        ).then(function (translated) {
          return base.search(translated);
        });
      },
    });
  }

  // ------------------------------------------------------------------
  // Boot
  // ------------------------------------------------------------------
  function boot() {
    initPredictive();
    var results = document.querySelector('[data-algolia-instantsearch]');
    if (results) initInstantSearch(results);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-bind predictive when the theme injects search UI dynamically (popup/drawer).
  document.addEventListener('shopify:section:load', initPredictive);
  document.addEventListener('click', function (e) {
    if (
      e.target.closest &&
      e.target.closest('.top-search-toggle, .header_search, [data-predictive-search]')
    ) {
      setTimeout(initPredictive, 100);
    }
  });

  window.AlgoliaTheme = {
    config: CFG,
    client: client,
    translateToEnglish: translateToEnglish,
    formatMoney: formatMoney,
    productCardHTML: productCardHTML,
    sendInsights: sendInsights,
  };
})();
